import React, { useState, useMemo, useEffect, useRef } from "react"
import Routes from "../../routes"

import { getEnvironment, getBrand, getTheme } from "@helpers/siteHelpers"
import { getRequest } from "@helpers/apiHelpers"
import { getBrandFromId } from "@helpers/dataHelpers"
import { ThemeProvider } from "styled-components"
import { PostcardThemeProvider, SimpleAlert } from "@smaller-earth/postcard-components"
import { BrowserRouter } from "react-router-dom"
import { BrandProvider } from "@context/BrandContext"
import { UserProvider } from "@context/UserContext"
import { PageContextProvider } from "@context/PageContext"
import { AssistantProvider } from "@context/AssistantContext"
import AssistantPanel from "./AssistantPanel"
import { positions as alertPositions, Provider as AlertProvider } from "react-alert"

import TagManager from "react-gtm-module"

import { isEmpty } from "lodash"
import { useCookies } from "react-cookie"

import { mergeFeatureOverrides } from "@hooks/useFeature"

import * as Fathom from "fathom-client"

const GtmId = "GTM-5JGZ3J5S"
const FathomSiteId = "INUAKNMR"

const alertOptions = {
  timeout: 5000,
  position: alertPositions.BOTTOM_RIGHT
}

const theme = {
  ...getTheme(),
  breakpoints: {
    mobile: 0,
    tablet: 769,
    desktop: 1025,
    widescreen: 1216,
    fullhd: 1408,
    mahoosive: 1600
  }
}

const home = { name: "Home", path: "/" }
const separator = { separator: true }
const notifications = { name: "Notifications", path: "/notifications" }
const settings = { name: "Settings", path: "/settings" }

const App = () => {
  const [cookies] = useCookies(["authState"])
  const [isLoaded, setIsLoaded] = useState(false)
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [user, setUser] = useState({})
  const [menu, setMenu] = useState({
    checklist: []
  })
  const [passwordUpdateRequired, setPasswordUpdateRequired] = useState(false)

  const parseMenu = (data, user) => {
    const { current_employer_id } = user

    const employers = data.employers
    employers.forEach((employer) => {
      employer.brand = getBrandFromId(employer.channel_id)
    })
    let currentEmployer = employers.find((employer) => employer.id === current_employer_id)
    if (currentEmployer === undefined) currentEmployer = null

    return {
      employer: currentEmployer,
      employers: employers,
      helpSupportUrl: data.help_support_url,
      communityUrl: data.community_url,
      messagingEnabled: data.messaging_enabled,
      messageCount: data.message_count,
      notificationCount: data.notification_count,
      staffCounts: data.staff_counts,
      showFeeSheet: data.show_fee_sheet,
      agentEnabled: data.agent_enabled,
      enableAddStaff: data.enable_add_staff,
      campfrontIntegrationEnabled: data.campfront_integration_enabled
    }
  }

  const parseChecklist = (data) => {
    let additionalLinks = []

    if (data.checklist.length > 0) {
      const incomplete_sections = data.checklist.filter((section) => section.percentage !== 100)
      const checklist = {
        name: `${getBrand().hostWord} Information`,
        status: data.profile_status,
        items: data.checklist,
        initiallyExpanded: data.profile_status !== "complete",
        profile_position: "outside",
        seperator: true,
        system_name: "information",
        completed: data.profile_status === "complete",
        link: incomplete_sections.length > 0 ? incomplete_sections[0].path : ""
      }

      additionalLinks.push(...[separator, checklist, separator, notifications])
    }

    if (data.messaging_enabled) {
      additionalLinks.push({
        name: "Messages",
        path: "/conversations",
        counter: data.message_count
      })
    }
    additionalLinks.push(settings)

    let menuItems = [].concat([home, separator], data.links, additionalLinks)
    if (data.help_support_url) menuItems.push({ name: "Help", path: data.help_support_url })
    return menuItems
  }

  useMemo(() => {
    if (isLoaded) return

    const sessionAuthState = sessionStorage["authState"]

    let authState
    let isAuthenticated = false
    let user = {}

    const setTracker = (user = null) => {
      if (isLoaded) return

      const environment = getEnvironment()
      if (environment !== "production") return

      // ignore if masquerading
      if (user && user.current_user_masquerading) return

      // init fathom
      Fathom.load(FathomSiteId, { spa: "auto" })

      // init GTM
      if (window.dataLayer) return
      TagManager.initialize({
        gtmId: GtmId,
        dataLayer:
          user && user.id
            ? {
                userId: user.id,
                email: user.email
              }
            : {}
      })
    }

    if (cookies.authState !== undefined) {
      // We are logged in
      authState = cookies.authState
      isAuthenticated = authState.isAuthenticated
      user = authState.user
    }

    if (!isAuthenticated && sessionAuthState) {
      // We are masquerading
      authState = JSON.parse(sessionAuthState)
      isAuthenticated = authState.isAuthenticated
      user = authState.user
    }

    if (authState) {
      const { passwordUpdateRequired } = authState

      setIsAuthenticated(isAuthenticated)
      setUser(user)
      setTracker(user)
      setPasswordUpdateRequired(passwordUpdateRequired)
    } else {
      setTracker()
    }

    setIsLoaded(true)
  }, [isLoaded, setIsLoaded, cookies])

  // Track previous dependency values to detect changes
  const prevDepsRef = useRef({
    user: null,
    isAuthenticated: null,
    cookies: null
  })

  useEffect(() => {
    if (!user || isEmpty(user) || !isAuthenticated) return

    // Detect which dependencies changed
    const changedDeps = []
    if (prevDepsRef.current.user !== user) {
      changedDeps.push("user")
    }
    if (prevDepsRef.current.isAuthenticated !== isAuthenticated) {
      changedDeps.push("isAuthenticated")
    }
    if (prevDepsRef.current.cookies !== cookies) {
      changedDeps.push("cookies")
    }

    // Update previous values
    prevDepsRef.current = {
      user,
      isAuthenticated,
      cookies
    }

    const switchedEmployerId = parseInt(cookies.switchedEmployerId)
    const currentEmployerId = parseInt(sessionStorage["currentEmployerId"])
    if (switchedEmployerId > 0 && currentEmployerId > 0 && currentEmployerId !== switchedEmployerId) {
      // Have we switched employer in another tab?
      sessionStorage["currentEmployerId"] = switchedEmployerId
      setTimeout(() => window.location.reload(), 750)
    }

    const { current_employer_id, access_token, current_user_masquerading } = user
    const triggerParam = changedDeps.length > 0 ? `&trigger=${changedDeps.join(",")}` : ""
    getRequest(
      `/employers/employers/${current_employer_id}/menu?&is_user_masquerading=${current_user_masquerading}${triggerParam}`,
      access_token
    )
      .then((data) => {
        setMenu({
          ...parseMenu(data, user),
          checklist: parseChecklist(data)
        })

        // Live feature overrides, so a channel being switched on (or back off)
        // reaches this session on its next page load rather than waiting for a
        // fresh login. mergeFeatureOverrides returns the same object when
        // nothing changed, which keeps this effect from looping on `user`.
        setUser((current) => mergeFeatureOverrides(current, data.features))

        sessionStorage["currentEmployerId"] = data.employer_id
      })
      .catch((error) => console.error(error))
  }, [user, isAuthenticated, cookies])

  return (
    <>
      {!isLoaded && <></>}

      {isLoaded && (
        <BrandProvider value={getBrand()}>
          <ThemeProvider theme={theme}>
            <PostcardThemeProvider theme={theme}>
              <AlertProvider template={SimpleAlert} {...alertOptions}>
                <UserProvider value={{ user, menu, setUser, setMenu }}>
                  <BrowserRouter>
                    <PageContextProvider>
                      <AssistantProvider>
                        <Routes isAuthenticated={isAuthenticated} passwordUpdateRequired={passwordUpdateRequired} />
                        <AssistantPanel />
                      </AssistantProvider>
                    </PageContextProvider>
                  </BrowserRouter>
                </UserProvider>
              </AlertProvider>
            </PostcardThemeProvider>
          </ThemeProvider>
        </BrandProvider>
      )}
    </>
  )
}

export default App
